import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Icon,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  alpha,
  useTheme,
} from "@mui/material";
import { IVoucherListItem } from "../../../accounting.interface";

interface VoucherConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText: string;
  severity: "warning" | "error" | "info";
  isLoading: boolean;
  details?: string[];
  voucher?: IVoucherListItem | null;
}

export const VoucherConfirmationDialog: React.FC<
  VoucherConfirmationDialogProps
> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  severity,
  isLoading,
  details = [],
  voucher,
}) => {
  const theme = useTheme();

  const severityConfig = {
    warning: {
      color: theme.palette.warning.main,
      bgcolor: alpha(theme.palette.warning.main, 0.1),
      icon: "warning",
    },
    error: {
      color: theme.palette.error.main,
      bgcolor: alpha(theme.palette.error.main, 0.1),
      icon: "error",
    },
    info: {
      color: theme.palette.info.main,
      bgcolor: alpha(theme.palette.info.main, 0.1),
      icon: "info",
    },
  };

  const config = severityConfig[severity];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ pb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: config.bgcolor,
              color: config.color,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon sx={{ fontSize: 24 }}>{config.icon}</Icon>
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pb: 2 }}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {message}
        </Typography>

        {voucher && (
          <Box
            sx={{
              p: 2,
              bgcolor: alpha(theme.palette.background.default, 0.5),
              borderRadius: 1,
              mb: 2,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Voucher Details:
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              ID:{" "}
              <span style={{ fontFamily: "monospace" }}>
                {voucher.voucher_id}
              </span>
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Type: {voucher.voucher_type} Voucher
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Amount: BHD{" "}
              {voucher.total_amount.toLocaleString("en-US", {
                minimumFractionDigits: 3,
                maximumFractionDigits: 3,
              })}
            </Typography>
            {voucher.beneficiary && (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Beneficiary: {voucher.beneficiary}
              </Typography>
            )}
          </Box>
        )}

        {details.length > 0 && (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Please note:
            </Typography>
            <List dense>
              {details.map((detail, index) => (
                <ListItem key={index} sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Icon sx={{ fontSize: 16, color: config.color }}>
                      fiber_manual_record
                    </Icon>
                  </ListItemIcon>
                  <ListItemText
                    primary={detail}
                    primaryTypographyProps={{
                      variant: "body2",
                      color: "text.secondary",
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={
            severity === "error"
              ? "error"
              : severity === "warning"
              ? "warning"
              : "primary"
          }
          onClick={onConfirm}
          disabled={isLoading}
          startIcon={
            isLoading ? (
              <CircularProgress size={16} />
            ) : (
              <Icon>
                {severity === "error"
                  ? "delete"
                  : severity === "warning"
                  ? "warning"
                  : "check"}
              </Icon>
            )
          }
        >
          {isLoading ? "Processing..." : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
